// Copyright (c) Docugami, Inc. All rights reserved.

import { Node, SuffixTree } from "./SuffixTree";

export interface TextSearchSettings {
    maxResultCount?: number;
}

export interface SearchResult {
    index: number;
}

const defaultSettings: TextSearchSettings = {}

export class TextSearch {
    private readonly _suffixTree: SuffixTree;

    constructor(text: string) {
        if (!text) {
            throw new Error("Cannot search an empty string");
        }

        this._suffixTree = new SuffixTree();

        // Add a termination character which is required for the suffix tree
        this._suffixTree.addString(text.toLowerCase() + '⚇');
    }

    search(query: string, settings: Partial<TextSearchSettings> = {}): SearchResult[] {
        const searchSettings: TextSearchSettings = {
            ...defaultSettings,
            ...settings
        };

        if (!query) {
            throw new Error("Cannot search for an empty string");
        }

        if (searchSettings.maxResultCount !== undefined && searchSettings.maxResultCount <= 0) {
            throw new Error("Invalid argument. maxResultsCount much be > 0");
        }

        return this.doSearch(query.toLowerCase(), searchSettings);
    }

    private doSearch(query: string, settings: TextSearchSettings): SearchResult[] {
        let node = this._suffixTree.root;
        let index = 0;

        let results: number[] = [];
        while (!!node) {
            const edge = node.transition[query[index]] ?? null;
            if (!edge) {
                break;
            }

            const [nextNode, start, end] = edge;
            node = nextNode;

            const result = this.traverseEdge(query, index, start, end);
            if (result === true) {
                // Found our query
                results = this.getResultsForNode(node, settings.maxResultCount);
                break;
            }
            else if (result === false) {
                // No matches
                break;
            }

            const edgeLength = this._suffixTree.getEdgeLength(start, end);
            index += edgeLength;
        }

        return results.map((x): SearchResult => { return { index: x }} );
    }

    private traverseEdge(query: string, index: number, start: number, end: number): boolean | null {
        for (let i = start; i <= end && !!query[index]; ++i, ++index) {
            if (query[index] !== this._suffixTree.text[i]) {
                // Not a match
                return false;
            }
        }

        if (index >= query.length) {
            return true;
        }

        // More text left to search
        return null;
    }

    private getResultsForNode(node: Node, maxResults?: number): number[] {
        const results: number[] = [];
        const searchQueue: Node[] = [node];
        while (searchQueue.length > 0) {
            const searchNode = searchQueue.shift()!;

            if (searchNode.isLeaf()) {
                results.push(searchNode.suffixIndex);

                if (!!maxResults && results.length >= maxResults) {
                    break;
                }
            }
            else {
                for (const [node] of Object.values(searchNode.transition)) {
                    searchQueue.push(node);
                }
            }
        }

        return results;
    }
}
